import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import BlueButton from "../../components/BlueButton";
import WhiteButton from "../../components/WhiteButton";
import GenericDatePicker from "../../components/GenericDatePicker";
import { IfPermissao, CHAVES_AUTOMACOES_CREATE } from "../../config/Context/PermissoesContext";
import NovaSolicitacaoModal from "./NovaSolicitacaoModal";
import { descreverErro } from "./mensagemErro.util";
import { aplicarRazoesSociais, cnpjsSemRazaoSocial } from "./razaoSocial.util";
import { listarSolicitacoesAutomacoes, resolverRazoesSociais } from "../../services/api-automacoes.service";
import styles from "./styles.module.css";

const TIPO_OPCOES = [
  { value: "esocial", label: "E-Social" },
  { value: "sped", label: "SPED" },
];
const STATUS_OPCOES = [
  { value: "pendente", label: "Pendente" },
  { value: "em_andamento", label: "Em andamento" },
  { value: "concluido", label: "Concluído" },
  { value: "erro", label: "Erro" },
];
const SPED_LABEL = { contabil: "Contábil", contribuicoes: "Contribuições", ecf: "ECF", fiscal: "Fiscal" };
const FLUXO_LABEL = { XML_PERIODO: "Eventos XML", RUBRICAS: "Rúbricas", FOLHA: "Folha" };
const STATUS_CHIP = {
  pendente: { label: "Pendente", bg: "#fff8e1", fg: "#f57c00" },
  em_andamento: { label: "Em andamento", bg: "#e3f2fd", fg: "#1565c0" },
  concluido: { label: "Concluído", bg: "#e8f5e9", fg: "#2e7d32" },
  erro: { label: "Erro", bg: "#ffebee", fg: "#c62828" },
};
const TIPO_CHIP = {
  esocial: { label: "E-Social", bg: "#e7edf7", fg: "#18316e" },
  sped: { label: "SPED", bg: "#e3f2fd", fg: "#1565c0" },
};

const COLUNAS = [
  { chave: "cnpj", rotulo: "CNPJ" },
  { chave: "razao_social", rotulo: "Razão Social" },
  { chave: "tipo", rotulo: "Tipo" },
  { chave: "periodo_inicio", rotulo: "Período" },
  { chave: "status", rotulo: "Status" },
  { chave: "criado_em", rotulo: "Criado em" },
];

// ---------- helpers ----------
const soDigitos = (v) => (v || "").replace(/\D/g, "");
function formatCnpj(v) {
  const d = soDigitos(v);
  if (d.length !== 14) return v;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}
const formatData = (iso) => {
  if (!iso) return "";
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
};
const formatPeriodo = (ini, fim) => (ini === fim ? formatData(ini) : `${formatData(ini)} – ${formatData(fim)}`);
const formatDataHora = (iso) => {
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ---------- particionamento (mesma regra dos RPAs) ----------
const DIA = 86_400_000;
const paraUtc = (iso) => {
  const [a, m, d] = iso.split("-").map(Number);
  return Date.UTC(a, m - 1, d);
};
const paraIso = (ms) => {
  const dt = new Date(ms);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
};
function regraParticao(s) {
  if (s.tipo === "esocial") {
    const lote = Math.min(31, Math.max(1, s.lote_dias || 31));
    return `1 execução, fatiada em lotes de até ${lote} dias (limite do e-Social)`;
  }
  if (s.sped_tipo === "contabil" || s.sped_tipo === "ecf") return "1 execução em lote → 1 arquivo por exercício (ano)";
  return "1 execução em lote → 1 arquivo por competência (mês)";
}
function particionar(s) {
  const inicio = paraUtc(s.periodo_inicio);
  const fim = paraUtc(s.periodo_fim);
  if (fim < inicio) return [];
  if (s.tipo === "esocial") {
    const lote = Math.min(31, Math.max(1, s.lote_dias || 31));
    const out = [];
    let cur = inicio, n = 1;
    while (cur <= fim) {
      const fimLote = Math.min(cur + (lote - 1) * DIA, fim);
      out.push({ rotulo: `Lote ${n}`, inicio: paraIso(cur), fim: paraIso(fimLote) });
      cur = fimLote + DIA;
      n += 1;
    }
    return out;
  }
  if (s.sped_tipo === "contabil" || s.sped_tipo === "ecf") {
    const out = [];
    const anoIni = new Date(inicio).getUTCFullYear();
    const anoFim = new Date(fim).getUTCFullYear();
    for (let ano = anoIni; ano <= anoFim; ano += 1) {
      out.push({
        rotulo: `Exercício ${ano}`,
        inicio: paraIso(Math.max(inicio, Date.UTC(ano, 0, 1))),
        fim: paraIso(Math.min(fim, Date.UTC(ano, 11, 31))),
      });
    }
    return out;
  }
  const out = [];
  const dFim = new Date(fim);
  const anoFim = dFim.getUTCFullYear();
  const mesFim = dFim.getUTCMonth();
  let ano = new Date(inicio).getUTCFullYear();
  let mes = new Date(inicio).getUTCMonth();
  while (ano < anoFim || (ano === anoFim && mes <= mesFim)) {
    out.push({
      rotulo: `Competência ${String(mes + 1).padStart(2, "0")}/${ano}`,
      inicio: paraIso(Math.max(inicio, Date.UTC(ano, mes, 1))),
      fim: paraIso(Math.min(fim, Date.UTC(ano, mes + 1, 0))),
    });
    mes += 1;
    if (mes > 11) { mes = 0; ano += 1; }
  }
  return out;
}

function ChipTipo({ tipo, spedTipo, tipoFluxo }) {
  const c = TIPO_CHIP[tipo];
  const detalhe = tipo === "sped" ? SPED_LABEL[spedTipo] : FLUXO_LABEL[tipoFluxo];
  return (
    <Box>
      <Chip label={c.label} size="small" sx={{ backgroundColor: c.bg, color: c.fg, fontWeight: 600, borderRadius: "16px" }} />
      {detalhe && <Typography sx={{ fontSize: 11, color: "#6b6e76", mt: "2px" }}>{detalhe}</Typography>}
    </Box>
  );
}
function ChipStatus({ status, mensagemErro }) {
  const c = STATUS_CHIP[status];
  const chip = <Chip label={c.label} size="small" sx={{ backgroundColor: c.bg, color: c.fg, fontWeight: 600, borderRadius: "16px" }} />;
  if (status === "erro" && mensagemErro) {
    return <Tooltip title={mensagemErro} arrow><span>{chip}</span></Tooltip>;
  }
  return chip;
}

export default function Automacoes() {
  const [fTipo, setFTipo] = useState(null);
  const [fStatus, setFStatus] = useState(null);
  const [fCnpj, setFCnpj] = useState("");
  const [fDataIni, setFDataIni] = useState(null);
  const [fDataFim, setFDataFim] = useState(null);

  const [ordenarPor, setOrdenarPor] = useState("criado_em");
  const [ordem, setOrdem] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [detalheClicado, setDetalhe] = useState(null);
  const [nomesPorCnpj, setNomesPorCnpj] = useState(null);
  const [novaAberta, setNovaAberta] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erroCarregar, setErroCarregar] = useState(null);

  const recarregar = useCallback(async (filtros = {}) => {
    setCarregando(true);
    setErroCarregar(null);
    try {
      const lista = await listarSolicitacoesAutomacoes(filtros);
      setSolicitacoes(lista);
    } catch (err) {
      console.warn("[automacoes] falha ao listar solicitações:", err?.message || err);
      setSolicitacoes([]);
      setErroCarregar("Não foi possível carregar as solicitações.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  // A API grava o CNPJ no lugar da razão social quando a busca dela falha (best-effort). Só vamos ao
  // Cadastro de Empresas pelos CNPJs que estão assim; sem ele a coluna segue mostrando o CNPJ. O
  // serviço guarda o que já resolveu, então recarregar a lista não repete as consultas.
  useEffect(() => {
    const pendentes = cnpjsSemRazaoSocial(solicitacoes);
    if (pendentes.length === 0) return undefined;
    let cancelado = false;
    resolverRazoesSociais(pendentes)
      .then((achados) => {
        if (!cancelado && achados.size > 0) setNomesPorCnpj((antes) => new Map([...(antes ?? []), ...achados]));
      })
      .catch((err) => console.warn("[automacoes] razão social indisponível:", err?.message || err));
    return () => { cancelado = true; };
  }, [solicitacoes]);
  const solicitacoesComNome = useMemo(
    () => aplicarRazoesSociais(solicitacoes, nomesPorCnpj),
    [solicitacoes, nomesPorCnpj]
  );
  // O modal guarda a linha tal como estava no clique; o nome pode chegar depois dele, então é aplicado aqui.
  const detalhe = useMemo(
    () => (detalheClicado ? aplicarRazoesSociais([detalheClicado], nomesPorCnpj)[0] : null),
    [detalheClicado, nomesPorCnpj]
  );

  const contagem = useMemo(() => {
    const c = { pendente: 0, em_andamento: 0, concluido: 0, erro: 0 };
    for (const s of solicitacoes) c[s.status] += 1;
    return c;
  }, [solicitacoes]);
  const STAT_CARDS = [
    { rotulo: "Concluído", n: contagem.concluido, cor: "#2e7d32" },
    { rotulo: "Erro", n: contagem.erro, cor: "#c62828" },
    { rotulo: "Pendente", n: contagem.pendente + contagem.em_andamento, cor: "#f57c00" },
  ];

  const filtradas = useMemo(() => {
    const alvoCnpj = soDigitos(fCnpj);
    const iniStr = fDataIni ? fDataIni.toISOString().slice(0, 10) : null;
    const fimStr = fDataFim ? fDataFim.toISOString().slice(0, 10) : null;
    const lista = solicitacoesComNome.filter((s) => {
      if (fTipo && s.tipo !== fTipo) return false;
      if (fStatus && s.status !== fStatus) return false;
      if (alvoCnpj && !s.cnpj.includes(alvoCnpj)) return false;
      if (iniStr && s.periodo_fim < iniStr) return false;
      if (fimStr && s.periodo_inicio > fimStr) return false;
      return true;
    });
    const dir = ordem === "desc" ? -1 : 1;
    return [...lista].sort((a, b) => {
      const va = a[ordenarPor] ?? "";
      const vb = b[ordenarPor] ?? "";
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [solicitacoesComNome, fTipo, fStatus, fCnpj, fDataIni, fDataFim, ordenarPor, ordem]);

  const paginadas = filtradas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const onOrdenar = (chave) => {
    if (ordenarPor === chave) setOrdem((o) => (o === "asc" ? "desc" : "asc"));
    else { setOrdenarPor(chave); setOrdem("asc"); }
  };
  const limparFiltros = () => {
    setFTipo(null); setFStatus(null); setFCnpj(""); setFDataIni(null); setFDataFim(null); setPage(0);
    recarregar();
  };

  const filhas = detalhe ? particionar(detalhe) : [];
  const diasEsocial = detalhe?.tipo === "esocial" ? Math.ceil(filhas.length / 10) : 0;
  const erroDetalhe = descreverErro(detalhe?.mensagem_erro);

  return (
    <div className={styles.container}>
      {/* cabeçalho */}
      <div className={styles.header}>
        <div className={styles.title}>
          <SmartToyOutlinedIcon sx={{ fontSize: "2rem", color: "#18316e" }} />
          <div>
            <h1 className={styles.titleText}>Automações</h1>
            <p className={styles.subtitle}>
              Solicitações de coleta automatizada (RPA) de arquivos E-Social e SPED
            </p>
          </div>
        </div>
        <div className={styles.statCards}>
          {STAT_CARDS.map((c) => (
            <div key={c.rotulo} className={styles.statCard} style={{ borderLeftColor: c.cor }}>
              <span className={styles.statNum} style={{ background: c.cor }}>{c.n}</span>
              <span className={styles.statLabel}>{c.rotulo}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ações */}
      <div className={styles.headerActions} style={{ justifyContent: "flex-end", marginBottom: "1rem" }}>
        <IfPermissao anyOf={CHAVES_AUTOMACOES_CREATE}>
          <BlueButton
            textValue="Nova solicitação"
            contentStartIcon={<AddIcon fontSize="small" />}
            clickFunction={() => setNovaAberta(true)}
          />
        </IfPermissao>
      </div>

      {/* filtros */}
      <Paper className={styles.filtrosCard} elevation={0}>
        <div className={styles.filtrosHeader}>
          <FilterListIcon className={styles.iconFilter} />
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Filtros Avançados</Typography>
        </div>
        <div className={styles.filtrosGrid}>
          <Autocomplete
            size="small" sx={{ width: 200 }} options={TIPO_OPCOES}
            getOptionLabel={(o) => o.label}
            value={TIPO_OPCOES.find((o) => o.value === fTipo) || null}
            onChange={(_, v) => { setFTipo(v?.value ?? null); setPage(0); }}
            renderInput={(p) => <TextField {...p} label="Tipo" />}
          />
          <Autocomplete
            size="small" sx={{ width: 200 }} options={STATUS_OPCOES}
            getOptionLabel={(o) => o.label}
            value={STATUS_OPCOES.find((o) => o.value === fStatus) || null}
            onChange={(_, v) => { setFStatus(v?.value ?? null); setPage(0); }}
            renderInput={(p) => <TextField {...p} label="Status" />}
          />
          <TextField
            size="small" sx={{ width: 180 }} label="CNPJ" placeholder="somente números"
            value={fCnpj} onChange={(e) => { setFCnpj(e.target.value); setPage(0); }}
          />
          <Box sx={{ width: 170 }}>
            <GenericDatePicker label="De" value={fDataIni} onChange={setFDataIni} clearable onClear={() => setFDataIni(null)} />
          </Box>
          <Box sx={{ width: 170 }}>
            <GenericDatePicker label="Até" value={fDataFim} onChange={setFDataFim} clearable onClear={() => setFDataFim(null)} />
          </Box>
        </div>
        <div className={styles.filtrosAcoes}>
          <BlueButton
            textValue="Filtrar"
            contentStartIcon={<SearchIcon fontSize="small" />}
            clickFunction={() => {
              setPage(0);
              recarregar({ tipo: fTipo || undefined, status: fStatus || undefined, cnpj: fCnpj || undefined });
            }}
          />
          <WhiteButton textValue="Limpar" iconLeft contentIcon={<CloseIcon fontSize="small" />} clickFunction={limparFiltros} />
        </div>
      </Paper>

      {erroCarregar && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erroCarregar}
        </Alert>
      )}

      {/* tabela */}
      <Paper className={styles.tableCard} elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {COLUNAS.map((c) => (
                  <TableCell key={c.chave} className={styles.thSort} onClick={() => onOrdenar(c.chave)}>
                    {c.rotulo}
                    {ordenarPor === c.chave && (
                      <ArrowDownwardIcon
                        sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle", transform: ordem === "asc" ? "rotate(180deg)" : "none" }}
                      />
                    )}
                  </TableCell>
                ))}
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginadas.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{formatCnpj(s.cnpj)}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{s.razao_social}</TableCell>
                  <TableCell><ChipTipo tipo={s.tipo} spedTipo={s.sped_tipo} tipoFluxo={s.tipo_fluxo} /></TableCell>
                  <TableCell>{formatPeriodo(s.periodo_inicio, s.periodo_fim)}</TableCell>
                  <TableCell><ChipStatus status={s.status} mensagemErro={s.mensagem_erro} /></TableCell>
                  <TableCell sx={{ color: "#6b6e76", fontSize: 13 }}>{formatDataHora(s.criado_em)}</TableCell>
                  <TableCell>
                    <BlueButton
                      textValue="Detalhes"
                      contentStartIcon={<ListAltIcon fontSize="small" />}
                      clickFunction={() => setDetalhe(s)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {carregando && <div className={styles.vazio}>Carregando solicitações…</div>}
        {!carregando && filtradas.length === 0 && (
          <div className={styles.vazio}>Nenhuma solicitação encontrada com os filtros aplicados.</div>
        )}

        <div className={styles.tableFooter}>
          <span className={styles.total}>Total: {filtradas.length} registro{filtradas.length === 1 ? "" : "s"}</span>
          <TablePagination
            component="div"
            count={filtradas.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </div>
      </Paper>

      {/* modal Detalhes */}
      <Dialog open={detalhe != null} onClose={() => setDetalhe(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ background: "linear-gradient(135deg, #0d2847 0%, #1e4a7a 100%)", color: "#fff", fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Solicitação #{detalhe?.id}
          <IconButton onClick={() => setDetalhe(null)} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detalhe && (
            <>
              <div className={styles.resumoGrid}>
                <div className={styles.resumoItem}>
                  <span className={styles.resumoLabel}>Empresa</span>
                  <span className={styles.resumoValor}>{detalhe.razao_social}</span>
                </div>
                <div className={styles.resumoItem}>
                  <span className={styles.resumoLabel}>CNPJ</span>
                  <span className={styles.resumoValor}>{formatCnpj(detalhe.cnpj)}</span>
                </div>
                <div className={styles.resumoItem}>
                  <span className={styles.resumoLabel}>Tipo</span>
                  <span className={styles.resumoValor}>
                    {detalhe.tipo === "esocial" ? `E-Social · ${FLUXO_LABEL[detalhe.tipo_fluxo] || ""}` : `SPED · ${SPED_LABEL[detalhe.sped_tipo] || ""}`}
                  </span>
                </div>
                <div className={styles.resumoItem}>
                  <span className={styles.resumoLabel}>Período</span>
                  <span className={styles.resumoValor}>{formatPeriodo(detalhe.periodo_inicio, detalhe.periodo_fim)}</span>
                </div>
                <div className={styles.resumoItem}>
                  <span className={styles.resumoLabel}>Status</span>
                  <ChipStatus status={detalhe.status} />
                </div>
              </div>

              {detalhe.status === "erro" && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="detalhe-erro">
                  <AlertTitle sx={{ fontWeight: 700 }}>Mensagem de erro</AlertTitle>
                  {erroDetalhe.legivel || "A execução terminou com erro, mas a central-rpas não informou o motivo."}
                  {erroDetalhe.tecnico && (
                    <Typography component="div" sx={{ mt: 1, fontSize: 11.5, color: "#6b6e76", wordBreak: "break-word" }}>
                      Detalhe técnico: {erroDetalhe.tecnico}
                    </Typography>
                  )}
                </Alert>
              )}

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  {detalhe.tipo === "esocial" ? "Lotes da execução" : "Arquivos da execução"}
                </Typography>
                <Chip label={filhas.length} size="small" sx={{ backgroundColor: "#e7edf7", color: "#18316e", fontWeight: 700 }} />
              </Box>
              <p className={styles.reqHint}>{regraParticao(detalhe)}.</p>

              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#f5f7fa", fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f7fa", fontWeight: 700 }}>Janela</TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f7fa", fontWeight: 700 }}>Período</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filhas.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell className={styles.reqRotulo}>{f.rotulo}</TableCell>
                        <TableCell>{formatPeriodo(f.inicio, f.fim)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {detalhe.tipo === "esocial" && filhas.length > 10 && (
                <p className={styles.reqHint} style={{ marginTop: 8 }}>
                  O e-Social processa no máximo <b>10 coletas por dia</b> (por CNPJ) — estes {filhas.length} lotes
                  são distribuídos ao longo de ≈ <b>{diasEsocial}</b> dia(s).
                </p>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <WhiteButton textValue="Fechar" clickFunction={() => setDetalhe(null)} />
        </DialogActions>
      </Dialog>

      <NovaSolicitacaoModal
        aberto={novaAberta}
        aoFechar={() => setNovaAberta(false)}
        aoCriar={(nova) => {
          // Prepend otimista (feedback imediato) + recarrega do backend
          // logo em seguida, que é a fonte da verdade.
          setSolicitacoes((prev) => [nova, ...prev]);
          recarregar();
        }}
      />
    </div>
  );
}
